import React, { useEffect, useState } from 'react';
import css from './SubscriptionPage.module.css';
import { useDispatch, useSelector } from 'react-redux';

import { FormattedMessage } from 'react-intl';
import EnhancedPaymentMethodsForm from '../PaymentMethodsPage/PaymentMethodsForm/PaymentMethodsForm';
import { ensureCurrentUser } from '../../util/data';
import { createStripeSetupIntent } from '../PaymentMethodsPage/PaymentMethodsPage.duck';
import {
  getClientSecret,
  getDefaultPaymentMethod,
  getPaymentParams,
  getUserDetails,
} from '../../util/userDataExtraction';

import { Button, Modal, SavedCardDetails } from '../../components';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { savePaymentMethod } from '../../ducks/paymentMethods.duck';
import {
  handleCardSetup,
  handleCardSetupErrorSelector,
  initiateSubscription,
} from '../../ducks/stripe.duck';
import { onTriggerError } from '../../util/toast';

const SubscriptionPaymentModal = ({
  isOpen,
  currentUser,
  selectedPlanId,
  isEditListingSubscriptionModal,
  setPaymentMethodModalState,
  setIsConfirmationModalOpen = () => {},
}) => {
  const dispatch = useDispatch();

  const { firstName, lastName } = !!currentUser?.id && getUserDetails(currentUser);
  const handleCardSetupError = useSelector(handleCardSetupErrorSelector);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useExistingCard, setUseExistingCard] = useState(true);

  const {
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
  } = useSelector(state => state.paymentMethods);
  const hasDefaultPaymentMethod = currentUser?.id && getDefaultPaymentMethod(currentUser);

  async function handleSubmit(params) {
    setIsSubmitting(true);

    try {
      const ensuredCurrentUser = ensureCurrentUser(currentUser);
      const stripeCustomer = ensuredCurrentUser.stripeCustomer;
      const { stripe, card, formValues } = params;

      if (!stripeCustomer) {
        const setupIntent = await dispatch(createStripeSetupIntent(params));
        const setupIntentClientSecret = getClientSecret(setupIntent);

        const paymentParams = getPaymentParams(currentUser, formValues);

        // Handle card setup
        const stripeParams = { stripe, card, setupIntentClientSecret, paymentParams };

        const result = await dispatch(handleCardSetup(stripeParams));

        const newPaymentMethod = result.setupIntent.payment_method;

        // Save the new payment method
        await dispatch(savePaymentMethod(stripeCustomer, newPaymentMethod));
      }

      // Create Stripe subscription

      await dispatch(
        initiateSubscription({
          priceId: selectedPlanId,
        })
      );

      // Close the modal and reset the submitting state
    } catch (error) {
      onTriggerError(error.message);
    } finally {
      setIsSubmitting(false);
      setPaymentMethodModalState(false);
      // Open confirmation modal
      setIsConfirmationModalOpen();
    }
  }

  const stripeCustomer = currentUser?.stripeCustomer;
  const savedCard = stripeCustomer?.defaultPaymentMethod?.attributes?.card || null;

  const renderFormContent =
    useExistingCard && savedCard ? (
      <div className={css.savedCardContainer}>
        <SavedCardDetails
          card={savedCard}
          onManageDisableScrolling={(componentId, disable) =>
            dispatch(manageDisableScrolling(componentId, disable))
          }
          onChange={() => setUseExistingCard(false)}
          hideRemoveCard
        />
        <Button
          className={css.subscribeButton}
          onClick={async () => {
            setIsSubmitting(true);
            try {
              await dispatch(initiateSubscription({ priceId: selectedPlanId }));
              setPaymentMethodModalState(false);
              setIsConfirmationModalOpen();
            } catch (e) {
              onTriggerError(e.message);
            } finally {
              setIsSubmitting(false);
            }
          }}
          disabled={isSubmitting}
        >
          <FormattedMessage id="SubscriptionPage.useCardAndSubscribe" />
        </Button>
      </div>
    ) : (
      <EnhancedPaymentMethodsForm
        className={css.paymentForm}
        formId="PaymentMethodsForm"
        initialValues={{ name: `${firstName} ${lastName}` }}
        onSubmit={handleSubmit}
        handleRemovePaymentMethod={{}}
        hasDefaultPaymentMethod={hasDefaultPaymentMethod}
        addPaymentMethodError={addPaymentMethodError}
        deletePaymentMethodError={deletePaymentMethodError}
        createStripeCustomerError={createStripeCustomerError}
        handleCardSetupError={handleCardSetupError}
        inProgress={isSubmitting}
        idSubscriptionPage={true}
        paymentMethodModalState={isOpen}
        saveActionMessage={<FormattedMessage id="SubscriptionPage.saveActionMessage" />}
      />
    );

  return (
    <div className={css.subscriptionPaymentFormModal}>
      <Modal
        id="SubscriptionPaymentFormModal"
        isOpen={isOpen}
        onClose={() => {
          setPaymentMethodModalState(false);
        }}
        usePortal
        contentClassName={css.modalContent}
        onManageDisableScrolling={(componentId, disableScrolling) =>
          dispatch(manageDisableScrolling(componentId, disableScrolling))
        }
        modalTitle={<FormattedMessage id="PaymentMethodsForm.paymentCardDetails" />}
        isEditListingSubscriptionModal={isEditListingSubscriptionModal}
      >
        {renderFormContent}
      </Modal>
    </div>
  );
};

export default SubscriptionPaymentModal;
